import React, { useCallback, useState } from 'react'
import {
  Button,
  Drawer,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

import { IFilterMapped } from './LeadListCancelFilter'
import DateRangePicker from '../../../../../../../components/DateRangePicker'
import useAxios from '../../../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../../../types/pagination'
import SelectSearch, {
  IOptionSelect,
} from '../../../../../../../components/SelectSearch'
import { IPipeline, IPipelineItem } from '../../../../../../../types/pipeline'
import { EExpiredDateStatus } from '../../../../../../../types/lead'
import PipelineSelectSearch from '../../../../../../../components/SelectSearch/PipelineSelectSearch'

interface LeadListCancelFilterDrawerProps {
  isOpen: boolean
  onClose: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
}

const menuItemsTypeDateExpire = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'ยังไม่หมดอายุ',
    value: EExpiredDateStatus.NOT_EXPIRED,
  },
  {
    label: 'หมดอายุแล้ว',
    value: EExpiredDateStatus.EXPIRED,
  },
]

const LeadListCancelFilterDrawer = ({
  isOpen,
  onClose,
  onFilterChange,
  filter,
  onSearch,
}: LeadListCancelFilterDrawerProps) => {
  const {
    page,
    perPage,
    search,
    startDate,
    endDate,
    pipelineObjectId,
    activePipelineItemObjectId,
    expiredDateStatus,
  } = filter

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchPipeline, setSearchPipeline] = useState<string>('')
  const [pipeline, setPipeline] = useState<IPipeline>()
  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  const handleReset = useCallback(() => {
    onFilterChange({
      page: page,
      perPage: perPage,
      search: undefined,
      startDate: undefined,
      endDate: undefined,
      pipelineObjectId: undefined,
      activePipelineItemObjectId: undefined,
      expiredDateStatus: undefined,
    })

    onSearch({
      page: page,
      perPage: perPage,
      search: undefined,
      startDate: undefined,
      endDate: undefined,
      pipelineObjectId: undefined,
      activePipelineItemObjectId: undefined,
      expiredDateStatus: undefined,
    })
  }, [page, perPage, onFilterChange, onSearch])

  const pipelineList = useAxios<PaginationResponseInterface<IPipeline>>({
    method: 'GET',
    url: '/pipeline',
    params: {
      page: 1,
      perPage: 5,
      search: searchPipeline,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchPipeline = async (value: string) => {
    setSearchPipeline(value)
    await pipelineList.mutate()
  }

  return (
    <Drawer
      anchor={'right'}
      keepMounted
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        style: {
          width: isMobile ? '80%' : '40%',
          maxWidth: isMobile ? '80%' : '40%',
        },
      }}
    >
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12} lg={12}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            label={'ค้นหา'}
            fullWidth
            id="search"
            name="search"
            autoFocus
            variant="filled"
            value={search || ''}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(startDate, endDate) => {
              handleFilterChange({ startDate, endDate })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={!!pipelineObjectId ? 6 : 12}>
          {/* <SelectSearch<IPipeline>
            label={'รายการ Pipeline'}
            onSelect={(e) => {
              handleFilterChange({ pipelineObjectId: e?.value.objectId })
              setPipeline(e?.value)
            }}
            search={searchPipeline}
            onSearch={handleSearchPipeline}
            options={
              pipelineList.data?.records.map((pipeline: IPipeline) => ({
                label: pipeline.name,
                value: pipeline,
              })) || ([] as IOptionSelect<IPipeline>[])
            }
          /> */}

          <PipelineSelectSearch
            multiple={false}
            label={'รายการ Pipeline'}
            onSelect={(e) => {
              handleFilterChange({ pipelineObjectId: e?.value.objectId })
              setPipeline(e?.value)
            }}
          />
        </Grid>
        {!!pipelineObjectId && (
          <Grid item xs={12} lg={6}>
            <FormControl variant="filled" fullWidth>
              <InputLabel id="activePipelineItemObjectId-label">
                สถานะ Pipeline
              </InputLabel>
              <Select
                id="activePipelineItemObjectId"
                name="activePipelineItemObjectId"
                labelId="activePipelineItemObjectId-label"
                value={activePipelineItemObjectId}
                onChange={(e) =>
                  handleFilterChange({
                    activePipelineItemObjectId: e.target.value as string,
                  })
                }
                fullWidth
              >
                <MenuItem disabled value="" selected>
                  <em>เลือกสถานะ Pipeline</em>
                </MenuItem>
                <MenuItem value={undefined} key={0}>
                  ทั้งหมด
                </MenuItem>
                {pipeline?.items.map((item: IPipelineItem, index: number) => (
                  <MenuItem value={item.objectId} key={index}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} lg={12}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="expiredDateStatus-label">วันหมดอายุ</InputLabel>
            <Select
              id="expiredDateStatus"
              name="expiredDateStatus"
              labelId="expiredDateStatus-label"
              value={expiredDateStatus}
              onChange={(e) =>
                handleFilterChange({
                  expiredDateStatus: e.target.value as EExpiredDateStatus,
                })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกวันหมดอายุ</em>
              </MenuItem>
              {menuItemsTypeDateExpire.map((item, index: number) => (
                <MenuItem value={item.value} key={index}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={9} lg={9}>
          <Button
            color="primary"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => onSearch(filter)}
            sx={{ height: '100%' }}
          >
            ค้นหา
          </Button>
        </Grid>
        <Grid item xs={3} lg={3}>
          <Button
            color="inherit"
            size="large"
            fullWidth
            variant="contained"
            startIcon={!isMobile && <ClearIcon />}
            onClick={() => handleReset()}
            sx={{ height: '100%' }}
          >
            {isMobile ? <ClearIcon /> : 'รีเซ็ต'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  )
}

export default LeadListCancelFilterDrawer
